import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserProfile } from '../store/slices/authSlice';
import {
  Container,
  Title,
  Card,
  Text,
  Badge,
  Stack,
  Group,
  Loader,
  Alert,
  Button,
  Avatar,
  Divider,
  SimpleGrid,
  Paper,
  Tabs
} from '@mantine/core';
import {
  IconExclamationCircle,
  IconMapPin,
  IconUser,
  IconStar,
  IconShoppingBag,
  IconMessage,
  IconSparkles,
  IconPackage
} from '@tabler/icons-react';
import { api } from '../services/api';
import SpecialRequestForm from '../components/special-requests/SpecialRequestForm';
import { notifications } from '@mantine/notifications';

const PrincessDetailPage = () => {
  const { princessId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector(state => state.auth);
  
  const [princess, setPrincess] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [specialRequestModalOpened, setSpecialRequestModalOpened] = useState(false);
  const [activeTab, setActiveTab] = useState('products');

  useEffect(() => {
    fetchPrincessDetails();
    // Refresh user profile to ensure we have latest subscription data
    if (isAuthenticated) {
      dispatch(fetchUserProfile());
    }
  }, [princessId, isAuthenticated, dispatch]);

  const fetchPrincessDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch princess details
      const princessResponse = await api.get(`/princesses/${princessId}/`);
      setPrincess(princessResponse);
      
      // Fetch princess's products
      const productsResponse = await api.get(`/products/?seller=${princessId}`);
      setProducts(productsResponse.products || []);
    } catch (err) {
      console.error('Error fetching princess details:', err);
      setError(err.message || 'Failed to load princess details');
    } finally {
      setLoading(false);
    }
  };

  const handleSpecialRequestSuccess = () => {
    notifications.show({
      title: 'Special Request Sent',
      message: 'Your special request has been submitted successfully!',
      color: 'green'
    });
    setSpecialRequestModalOpened(false);
  };

  const handleMessagePrincess = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/messages/${princess.user_id}`);
  };

  const canMakeSpecialRequest = () => {
    // Check if user has PayPiggyPro subscription
    return user?.subscription?.plan === 'paypiggiepro' && user?.subscription?.status === 'active';
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  if (error || !princess) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
          {error || 'Princess not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Princess Header */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group align="flex-start">
            <Avatar
              src={princess.primary_profile_image}
              size={120}
              radius="md"
              color="blue"
            >
              <IconUser size="3rem" />
            </Avatar>
            
            <div style={{ flex: 1 }}>
              <Group justify="space-between">
                <div>
                  <Group gap="sm">
                    <Title order={2}>{princess.business_display_name}</Title>
                    {princess.is_verified && (
                      <Badge color="green" variant="light">
                        Verified
                      </Badge>
                    )}
                  </Group>
                  <Text size="md" c="dimmed">@{princess.user_username}</Text>
                </div>
                
                <Group>
                  <Button
                    leftSection={<IconMessage size="1rem" />}
                    variant="light"
                    onClick={handleMessagePrincess}
                  >
                    Message
                  </Button>
                  
                  <Button
                    leftSection={<IconSparkles size="1rem" />}
                    onClick={() => setSpecialRequestModalOpened(true)}
                    variant="gradient"
                    gradient={{ from: 'blue', to: 'cyan', deg: 45 }}
                  >
                    Special Request
                  </Button>
                </Group>
              </Group>
              
              <Text mt="md">{princess.business_description || 'No description available'}</Text>
              
              <Group mt="md" gap="xl">
                <Group gap="xs">
                  <IconMapPin size="1rem" color="var(--mantine-color-gray-6)" />
                  <Text size="sm" c="dimmed">
                    {princess.business_city}, {princess.business_state}
                  </Text>
                </Group>
                
                <Group gap="xs">
                  <IconStar size="1rem" color="var(--mantine-color-gray-6)" />
                  <Text size="sm" c="dimmed">
                    {princess.average_rating > 0 ? `${princess.average_rating}/5 (${princess.total_reviews} reviews)` : 'No reviews yet'}
                  </Text>
                </Group>
                
                <Group gap="xs">
                  <IconShoppingBag size="1rem" color="var(--mantine-color-gray-6)" />
                  <Text size="sm" c="dimmed">
                    {princess.total_orders} orders completed
                  </Text>
                </Group>
              </Group>
            </div>
          </Group>
        </Card>

        {/* Content Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="products" leftSection={<IconPackage size="1rem" />}>
              Products ({products.length})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="products" pt="lg">
            {products.length === 0 ? (
              <Paper p="xl" withBorder>
                <Text ta="center" c="dimmed">
                  No products available yet
                </Text>
              </Paper>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
                {products.map((product) => (
                  <Card
                    key={product.id}
                    shadow="sm"
                    padding="lg"
                    radius="md"
                    withBorder
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/products/${product.id}`)}
                  >
                    {product.primary_image && (
                      <Card.Section>
                        <img
                          src={product.primary_image}
                          alt={product.title}
                          style={{ width: '100%', height: 200, objectFit: 'cover' }}
                        />
                      </Card.Section>
                    )}
                    
                    <Stack mt="md" gap="xs">
                      <Text fw={500} lineClamp={1}>{product.title}</Text>
                      <Text size="xl" fw={700} c="blue">
                        ${product.price}
                      </Text>
                      <Badge color={product.status === 'active' ? 'green' : 'gray'} variant="light">
                        {product.status}
                      </Badge>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Tabs.Panel>
        </Tabs>

        {/* Special Request Modal */}
        {princess && (
          <SpecialRequestForm
            opened={specialRequestModalOpened}
            onClose={() => setSpecialRequestModalOpened(false)}
            princessId={princess.id}
            princessName={princess.business_display_name}
            onSuccess={handleSpecialRequestSuccess}
          />
        )}
      </Stack>
    </Container>
  );
};

export default PrincessDetailPage;